import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Check, CircleAlert, Loader2, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { AuthFrame } from '@/components/auth-frame'
import { PasswordInput } from '@/components/password-input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import { meQuery, useChangePassword, useSignOut } from '@/lib/session'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/change-password')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) throw redirect({ to: '/login' })
    return { me }
  },
  component: ChangePasswordPage,
})

function ChangePasswordPage() {
  useTitle('Set your password')
  const { me } = Route.useRouteContext()
  const change = useChangePassword()
  const signOut = useSignOut()
  const navigate = useNavigate()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const local = me.email.split('@')[0]!.toLowerCase()
  const rules = [
    { ok: next.length >= 12, label: 'At least 12 characters' },
    {
      ok: next.length > 0 && !(local.length >= 4 && next.toLowerCase().includes(local)),
      label: 'Does not include your email name',
    },
    { ok: next.length > 0 && next !== current, label: 'Different from your current password' },
    { ok: confirm.length > 0 && confirm === next, label: 'Both new passwords match' },
  ]
  const ready = current.length > 0 && rules.every((r) => r.ok)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    await change.mutateAsync({ currentPassword: current, newPassword: next })
    toast.success('Password saved')
    navigate({ to: '/', replace: true })
  }

  return (
    <AuthFrame
      title={me.mustChangePassword ? 'Set your own password' : 'Change your password'}
      intro={
        me.mustChangePassword
          ? 'You signed in with a temporary password. Choose a new one to continue. Other devices will be signed out.'
          : 'Other devices will be signed out when you save.'
      }
    >
      <form onSubmit={(e) => void onSubmit(e).catch(() => {})} noValidate>
        <FieldGroup className="gap-5">
          {change.error && (
            <Alert variant="destructive" role="alert">
              <CircleAlert />
              <AlertDescription>{change.error.message}</AlertDescription>
            </Alert>
          )}
          <input type="email" autoComplete="username" value={me.email} readOnly hidden />
          <Field>
            <FieldLabel htmlFor="current">
              {me.mustChangePassword ? 'Temporary password' : 'Current password'}
            </FieldLabel>
            <PasswordInput
              id="current"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="new">New password</FieldLabel>
            <PasswordInput
              id="new"
              autoComplete="new-password"
              aria-describedby="password-rules"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="confirm">Confirm new password</FieldLabel>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          <ul id="password-rules" className="space-y-1.5 text-sm" aria-live="polite">
            {rules.map((r) => (
              <li
                key={r.label}
                className={cn(
                  'flex items-center gap-2',
                  r.ok ? 'text-success' : 'text-muted-foreground',
                )}
              >
                {r.ok ? (
                  <Check className="size-4" aria-hidden />
                ) : (
                  <X className="size-4" aria-hidden />
                )}
                <span>
                  {r.label}
                  <span className="sr-only">{r.ok ? ' (done)' : ' (not yet)'}</span>
                </span>
              </li>
            ))}
          </ul>
          <Button
            type="submit"
            className="h-11 w-full text-[15px]"
            disabled={!ready || change.isPending}
          >
            {change.isPending && <Loader2 className="animate-spin" />}
            Save password
          </Button>
        </FieldGroup>
      </form>
      <button
        type="button"
        className="mt-6 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        onClick={() => signOut.mutate(undefined, { onSettled: () => navigate({ to: '/login' }) })}
      >
        Sign out instead
      </button>
    </AuthFrame>
  )
}
